import React, { useEffect, useMemo, useState } from 'react';
import {
  getAnalyticsConsentStatus,
  setAnalyticsConsent,
  initGA,
  trackEvent,
  OPEN_ANALYTICS_CONSENT_EVENT,
} from '../utils/analytics';

const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(() => getAnalyticsConsentStatus() === 'unknown');
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => getAnalyticsConsentStatus() === 'granted');

  useEffect(() => {
    const handleOpen = () => setIsVisible(true);
    window.addEventListener(OPEN_ANALYTICS_CONSENT_EVENT, handleOpen);

    return () => {
      window.removeEventListener(OPEN_ANALYTICS_CONSENT_EVENT, handleOpen);
    };
  }, []);

  const legalLinks = useMemo(() => ([
    { href: '/cerez-politikasi', label: 'Çerez Politikası' },
    { href: '/kvkk-aydinlatma-metni', label: 'KVKK Aydınlatma Metni' },
  ]), []);

  const acceptAnalytics = () => {
    setAnalyticsConsent('granted');
    initGA();
    trackEvent('analytics_consent_updated', {
      event_category: 'Consent',
      status: 'granted',
    });
    setIsVisible(false);
  };

  const denyAnalytics = () => {
    setAnalyticsConsent('denied');
    setIsVisible(false);
  };

  const savePreferences = () => {
    if (analyticsEnabled) {
      acceptAnalytics();
      return;
    }

    denyAnalytics();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[1200] border-t border-gray-200 bg-white/95 backdrop-blur-sm shadow-[0_-8px_30px_rgba(15,23,42,0.08)]">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-8">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-gray-900">Analitik Çerez Onayı</p>
          <p className="text-sm text-gray-600">Dönüşüm ve performans ölçümü için analitik çerezleri kullanmak istiyoruz.</p>
          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
            {legalLinks.map((link) => (
              <a key={link.href} href={link.href} className="underline hover:text-gray-700">
                {link.label}
              </a>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPreferences((prev) => !prev)}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Tercihleri Yönet
          </button>
          <button
            type="button"
            onClick={denyAnalytics}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Reddet
          </button>
          <button
            type="button"
            onClick={acceptAnalytics}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Kabul Et
          </button>
        </div>
      </div>

      {showPreferences && (
        <div className="border-t border-gray-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-4 md:px-8">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Çerez Tercihleri</p>
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Zorunlu Çerezler</p>
                  <p className="text-xs text-gray-500">Site fonksiyonları için her zaman açık kalır.</p>
                </div>
                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">Her Zaman Açık</span>
              </div>
            </div>

            <div className="mt-2 rounded-xl border border-gray-200 p-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Analitik Çerezler</p>
                  <p className="text-xs text-gray-500">Randevu dönüşümleri ve performans ölçümü için kullanılır.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analyticsEnabled}
                  onClick={() => setAnalyticsEnabled((prev) => !prev)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${analyticsEnabled ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${analyticsEnabled ? 'translate-x-5' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={savePreferences}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                Tercihleri Kaydet
              </button>
              <button
                type="button"
                onClick={() => setShowPreferences(false)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsentBanner;
